import moment, { Moment } from 'moment';
import { ExtraFeaturesKey } from '../../../../services/product/types/extra-features-key';
import { PlanNames } from '../../../../services/product/types/plan-names';

export interface CalendarTiers {
  disabledRange: Moment;
  priceTiers: {
    upto: Moment;
    price: number;
    name: string;
  }[];
}

const PLANS_FEATURES: {
  [key in PlanNames]: Record<ExtraFeaturesKey, boolean>;
} = {
  beginner: {
    'RELEASE.PAY_AS_YOU_GO': false,
    'RELEASE.DATE': false,
    'RELEASE.STORE_MAXIMIZER': true,
    'RELEASE.PRE_ORDER': true,
    'RELEASE.INSTANT_GRATIFICATION': true,
    'RELEASE.YOUTUBE_CONTENT_ID': true,
    'RELEASE.APPLE_DISTRIBUTION': true,
    'RELEASE.REMOVE_BRANDING': true,
  },
  growth: {
    'RELEASE.PAY_AS_YOU_GO': true,
    'RELEASE.DATE': false,
    'RELEASE.STORE_MAXIMIZER': true,
    'RELEASE.PRE_ORDER': true,
    'RELEASE.INSTANT_GRATIFICATION': true,
    'RELEASE.YOUTUBE_CONTENT_ID': true,
    'RELEASE.APPLE_DISTRIBUTION': true,
    'RELEASE.REMOVE_BRANDING': true,
  },
  pro: {
    'RELEASE.PAY_AS_YOU_GO': true,
    'RELEASE.DATE': false,
    'RELEASE.STORE_MAXIMIZER': true,
    'RELEASE.PRE_ORDER': true,
    'RELEASE.INSTANT_GRATIFICATION': true,
    'RELEASE.YOUTUBE_CONTENT_ID': true,
    'RELEASE.APPLE_DISTRIBUTION': true,
    'RELEASE.REMOVE_BRANDING': true,
  },
  'distribution pro': {
    'RELEASE.PAY_AS_YOU_GO': true,
    'RELEASE.DATE': false,
    'RELEASE.STORE_MAXIMIZER': true,
    'RELEASE.PRE_ORDER': true,
    'RELEASE.INSTANT_GRATIFICATION': true,
    'RELEASE.YOUTUBE_CONTENT_ID': true,
    'RELEASE.APPLE_DISTRIBUTION': true,
    'RELEASE.REMOVE_BRANDING': true,
  },
  'trac pro': {
    'RELEASE.PAY_AS_YOU_GO': true,
    'RELEASE.DATE': false,
    'RELEASE.STORE_MAXIMIZER': true,
    'RELEASE.PRE_ORDER': true,
    'RELEASE.INSTANT_GRATIFICATION': true,
    'RELEASE.YOUTUBE_CONTENT_ID': true,
    'RELEASE.APPLE_DISTRIBUTION': true,
    'RELEASE.REMOVE_BRANDING': true,
  },
  premium: {
    'RELEASE.PAY_AS_YOU_GO': true,
    'RELEASE.DATE': false,
    'RELEASE.STORE_MAXIMIZER': true,
    'RELEASE.PRE_ORDER': true,
    'RELEASE.INSTANT_GRATIFICATION': true,
    'RELEASE.YOUTUBE_CONTENT_ID': true,
    'RELEASE.APPLE_DISTRIBUTION': true,
    'RELEASE.REMOVE_BRANDING': true,
  },
};

export const hasPlanFeature = (
  plan?: PlanNames,
  feature?: ExtraFeaturesKey
) => {
  return plan && feature && PLANS_FEATURES[plan]
    ? PLANS_FEATURES[plan][feature]
    : false;
};

const DISTRIBUTION_BASE_PRICE = 300;
const DISTRIBUTION_TRACK_PRICE = 100;
const DISTRIBUTION_MAX_PRICE = 1000;

export const getPlanFeaturePrice = (
  _plan: PlanNames | undefined,
  feature: ExtraFeaturesKey,
  tracks?: number
) => {
  switch (feature) {
    case 'RELEASE.PAY_AS_YOU_GO': {
      const total =
        DISTRIBUTION_BASE_PRICE +
        ((tracks || 1) - 1) * DISTRIBUTION_TRACK_PRICE;
      return {
        price: total > DISTRIBUTION_MAX_PRICE ? DISTRIBUTION_MAX_PRICE : total,
        name: 'pay-as-you-go',
      };
    }
    default:
      return null;
  }
};

export const getFeaturePrice = (
  feature: ExtraFeaturesKey,
  condition: boolean,
  plan: PlanNames | undefined,
  isActive: boolean,
  tracks?: number
) => {
  if (!isActive || (!hasPlanFeature(plan, feature) && condition)) {
    return getPlanFeaturePrice(isActive ? plan : 'beginner', feature, tracks);
  }
  return null;
};

export const getCalendarTiers = (_trackCount: number): CalendarTiers => {
  const today = moment().startOf('day');

  const disabledRange = moment(today).startOf('day').add(24, 'hours');

  const tiers: CalendarTiers = {
    disabledRange,
    priceTiers: [
      {
        upto: moment(disabledRange).add(2, 'days'),
        price: 500,
        name: 'date-tier-1',
      },
      {
        upto: moment(disabledRange).add(3, 'days'),
        price: 400,
        name: 'date-tier-2',
      },
      {
        upto: moment(disabledRange).add(4, 'days'),
        price: 300,
        name: 'date-tier-3',
      },
      {
        upto: moment(disabledRange).add(5, 'days'),
        price: 200,
        name: 'date-tier-4',
      },
      {
        upto: moment(disabledRange).add(6, 'days'),
        price: 100,
        name: 'date-tier-5',
      },
    ],
  };

  return tiers;
};

export const getPriceTier = (
  day: Moment,
  priceTiers: CalendarTiers['priceTiers']
) => {
  if (day) {
    const tier = priceTiers.find((t) => day.isBefore(t.upto, 'day'));

    if (tier) {
      return tier;
    }
  }
};
